import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { of, catchError, switchMap, takeUntil, tap } from 'rxjs';
import { CmsService, Page } from '@spartacus/core';

import { ArgumentsLayoutEnum, IArgumentWithConfig, SdArgumentsComponent, IArguments } from '@seb/web-design-system/arguments';
import {
  BrandsEnum,
  BrandThemesEnum,
  ImageRatioEnum,
  PositionEnum,
  SdSpyTargetDirective,
  ThemesEnum
} from '@seb/web-design-system/core';
import {
  ArgumentsComponentInterface,
  argumentsSkeleton,
  DcpListResponse,
  RecipeSearchItem
} from '../../../models';
import { AbstractComponent } from '../abstract-component.component';
import { ComponentService } from '../../../services/component.service';
import { ArgumentMapper } from '../../../mappers/argument.mapper';
import { BrandCornerMapper } from '../../../mappers/brand-corner.mapper';
import { ChatService } from '../../../services/chat.service';
import { RecipeService } from '../../../services/recipe.service';
import { TrackingService } from '../../../services/tracking.service';
import { ThemesMapper } from '../../../mappers/themes.mapper';
import { transformHeadlineLevelToTitleLevel } from '../../../mappers/title-level.mapper';
import { HobStateService } from '@seb/web-common/core/services';

@Component({
  selector: 'wc-arguments',
  templateUrl: './arguments.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdArgumentsComponent,
    SdSpyTargetDirective
  ]
})
export class ArgumentsComponent extends AbstractComponent<ArgumentsComponentInterface> implements OnInit {
  arguments: IArguments;
  layoutTextConfig: PositionEnum.left | PositionEnum.right | PositionEnum.center;
  textConfig: PositionEnum;
  themesConfig: ThemesEnum | BrandThemesEnum;
  titleLevel: number;
  isSmall: boolean;
  imageRatio: ImageRatioEnum;
  layout: ArgumentsLayoutEnum;
  hasGradient: boolean;
  lastExec = 0;
  activeBrand: BrandsEnum;
  isHob: boolean;

  private skeletonUid: string;

  constructor(
    private componentService: ComponentService,
    private cdRef: ChangeDetectorRef,
    private argumentMapper: ArgumentMapper,
    private trackingService: TrackingService,
    private brandCornerMapper: BrandCornerMapper,
    private cmsService: CmsService,
    private recipeService: RecipeService,
    private readonly chatService: ChatService,
    private readonly hobStateService: HobStateService
  ) {
    super();
  }

  private skeletonInit(cmp: ArgumentsComponentInterface) {
    this.layout = this.argumentMapper.mapLayout(cmp.layout);
    this.layoutTextConfig = this.argumentMapper.mapLayoutTextConfig(cmp.layoutTextConfig);
    this.textConfig = this.argumentMapper.mapTextConfig(cmp.textConfig);
    this.isSmall = cmp.isSmall === 'true';
    this.imageRatio = this.argumentMapper.argumentsImageRatioEnumToImageRatioEnum(cmp.imageRatio);
    this.hasGradient = cmp.hasGradient ? JSON.parse(cmp.hasGradient) : false;
    if (this.useLazyLoad) {
      this.arguments = argumentsSkeleton;
    }
    this.cdRef.markForCheck();
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.hobStateService.hobState$.subscribe(({ isHob, liveBrand, siteBrand }) => {
        this.activeBrand = liveBrand || siteBrand;
        this.isHob = isHob;
        this.cdRef.markForCheck();
      })
    );

    this.component$
      .pipe(
        tap((cmp: ArgumentsComponentInterface) => {
          if (cmp.uid !== this.skeletonUid) {
            this.skeletonUid = cmp.uid;
            this.skeletonInit(cmp);
          }
        }),
        this.componentService.reloadComponentForArrayProperties(['pictos']),
        switchMap((cmp: ArgumentsComponentInterface) => {
          return cmp.links?.length > 0 ? this.componentService.getLazyComponentData(cmp, 'links') : of(cmp);
        }),
        switchMap((cmp: ArgumentsComponentInterface) => {
          return cmp.selectorImageComponents?.length > 0
            ? this.componentService.getLazyComponentData(cmp, 'selectorImageComponents')
            : of(cmp);
        }),
        tap((cmp: ArgumentsComponentInterface) => {
          this.themesConfig = ThemesMapper.mapBrandTheme(cmp.theme);
          this.titleLevel = transformHeadlineLevelToTitleLevel(cmp.headlineLevel);
        }),
        switchMap((cmp: ArgumentsComponentInterface) => {
          return this.argumentMapper.argumentsComponentToIArgumentWithConfig(
            cmp,
            this.brandCornerMapper.toWcConfig(this.brandCorner)?.isArgumentUnderlined
          );
        }
        ),
        switchMap((cmp: IArgumentWithConfig) => {
          return this.cmsService.getCurrentPage()?.pipe(
            switchMap((page) => {
              cmp.contentData.hasAlphaHeader = this.checkAlphaHeader(page);
              if (page?.template === 'RecipeCategoryPageTemplate') {
                return this.recipeService
                  .search('', 0, 12, {
                    'filters': [
                      {
                        'field': 'domain.key',
                        'values': [
                          page['externalRecipeCategoryFilter']
                        ]
                      }
                    ]
                  })
                  .pipe(
                    catchError(() => of(null)),
                    switchMap((recipes: DcpListResponse<RecipeSearchItem>) => {
                      const recipesNumber = recipes?.page?.totalElements;
                      const _subtitleLabel = cmp?.contentData?.content?.heading?.data?.subtitle.replace(
                        '{recipes_size}',
                        recipesNumber.toString()
                      );
                      cmp.contentData.content.heading.data.subtitle = _subtitleLabel;
                      return of(cmp);
                    })
                  );
              } else {
                return of(cmp);
              }
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((argument: IArgumentWithConfig) => {
        this.arguments = argument.contentData;
        this.setLoaded();
        this.cdRef.markForCheck();
      });
  }

  onVideoAction(event: { status: string; title: string }): void {
    if (Date.now() - this.lastExec < 3000) {
      return;
    }
    this.lastExec = Date.now();
    this.trackingService.sendEvent('clickVideo', {
      videoZone: '',
      videoAction: event.status,
      videoName: event.title
    });
  }

  checkAlphaHeader(page: Page) {
    return page && page['headerDesign'] ? page['headerDesign'] === 'ALPHA' && this.layout === ArgumentsLayoutEnum.embedded : false;
  }

  onClicked(event) {
    if (event?.url === '<<chat>>') {
      this.chatService.clickOpenChat();
    }
  }
}
